import React, { Suspense, useEffect, useRef, useState } from 'react';
import {
  Button,
  Spinner,
  Alert,
  Container,
  Card,
  CardBody,
  Badge,
  Modal,
  ModalBody,
  Table,
  Input,
  Label,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ModalHeader,
  CardHeader,
  ModalFooter,
  FormGroup,
  Progress,
} from 'reactstrap';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';
import {
  ChildSubTaskViewDTO,
  getAllProjects,
  getChildSubTaskById,
  getChildSubTaskLogs,
  getChildSubTasksBySubTask,
  getComments,
  getSubTaskLogs,
  getTaskLogs,
  getTeamLeadProjects,
  getTeammember,
  getTLProjectDetails,
  getUnreadCount,
  OverallLogDTO,
} from '../../Api/ApiUrlConst';
import { getMyProjects } from "../../Api/ApiUrlConst";
import { getProjectDetails } from "../../Api/ApiUrlConst";
import { useAuth } from '../../Context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import { Edit as EditIcon } from 'lucide-react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HttpsIcon from '@mui/icons-material/Https';
import { AddAlarm, Chat, ChatBubbleOutline, DownloadForOffline, HelpOutline, MarkChatUnread, Settings } from '@mui/icons-material';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Tooltip } from '@mui/material';
import AdminTaskSettings from './AdminTaskSettings';


// --- Interfaces ---
interface ProjectDTO {
  id: number;
  projectID: string;
  projectName: string;
  createdByName: string;
  startDate: string;
  endDate: string;
  taskCount: number;
  tasks?: TaskDTO[];
}
interface TaskDTO {
  id: number;
  description: string;
  projectId: number;
  projectName: string;
  assignedToId: string;
  assignedToName: string;
  createdById: string;
  createdByName: string;
  dueDate: string;
  completionDate: string;
  status: string;
  priority: string;
  percentCompleted: number;
  remarks: string;
  issues: string;
  isDueDateLocked: boolean;
  subTasks: SubTaskDTO[];
  spentHours?: number;
  maxHours?: number;
  isClientInputEnabled: boolean;
  totalSpent?: number;   // ✅ ADD
  totalMax?: number;     // ✅ ADD

}
interface SubTaskDTO {
  id: number;
  taskId: number;
  parentSubTaskId: number | null;
  description: string;
  assignedToId: string;
  createdByName: string;
  status: string;
  priority: string;
  dueDate: string;
  percentCompleted: number;
  workHours: number;
  completionDate: string | null;
  remarks: string;
  issues: string;
  isDueDateLocked: boolean;
  spentHours?: number;
  maxHours?: number;
  isClientInputEnabled: boolean;
  totalSpent?: number;   // ✅ ADD
  totalMax?: number;     // ✅ ADD

}
interface TeamMemberDTO {
  userID: string;
  name: string;
  department: string;
}
interface SortConfig {
  key: string | null;
  direction: 'asc' | 'desc';
}
const Comments = React.lazy(() => import('../CommentComponent/Comments'));
const ClientInputModal = React.lazy(() => import('../ClientInput/ClientInputModal'));

//#region status Maps
const statusColorMap: Record<string, string> = {
  'Yet to Start': 'status-yet-to-start',
  YetToStart: 'status-yet-to-start',

  'On Hold': 'status-on-hold',
  OnHold: 'status-on-hold',

  'Waiting for Input': 'status-waiting-input',
  WaitingForInput: 'status-waiting-input',

  'Modelling in Progress': 'status-modelling-progress',
  ModellingInProgress: 'status-modelling-progress',

  'Modelling ToDo': 'status-modelling-Todo',
  ModellingTodo: 'status-modelling-Todo',

  'Modelling Completed': 'status-modelling-completed',
  ModellingCompleted: 'status-modelling-completed',

  'Drafting in Progress': 'status-drafting-progress',
  DraftingInProgress: 'status-drafting-progress',

  'Drafting ToDo': 'status-drafting-Todo',
  DraftingTodo: 'status-drafting-Todo',

  'Drafting Completed': 'status-drafting-completed',
  DraftingCompleted: 'status-drafting-completed',

  'QC ToDo': 'status-qc-Todo',
  QCTodo: 'status-qc-Todo',

  'QC in Progress': 'status-qc-progress',
  QCInProgress: 'status-qc-progress',

  'QC Completed': 'status-qc-completed',
  QCCompleted: 'status-qc-completed',

  'Client Review in Progress': 'status-client-review-progress',
  ClientReviewInProgress: 'status-client-review-progress',

  'Client Review ToDo': 'status-client-review-Todo',
  ClientReviewTodo: 'status-client-review-Todo',



  'Client Review Completed': 'status-client-review-completed',
  ClientReviewCompleted: 'status-client-review-completed',

  'Revision in Progress': 'status-Revision-progress',
  RevisionInProgress: 'status-Revision-progress',

  'Revision ToDo': 'status-Revision-Todo',
  RevisionTodo: 'status-Revision-Todo',

  'Revision Completed': 'status-Revision-completed',
  RevisionCompleted: 'status-Revision-completed',


  'Final Correction in Progress': 'status-final-correction-progress',
  FinalCorrectionInProgress: 'status-final-correction-progress',

  'Final Correction ToDo': 'status-final-correction-Todo',
  FinalCorrectionTodo: 'status-final-correction-Todo',

  'Final Correction Completed': 'status-final-correction-completed',
  FinalCorrectionCompleted: 'status-final-correction-completed',

  'Not In Scope': 'status-Not-In-Scope',
  NotInScope: 'status-Not-In-Scope',

  Released: 'status-released',
  'Completed & Closed': 'status-completed',
  Completed: 'status-completed',

  Cancelled: 'status-cancelled',
  Issues: 'status-issues',

 CompletedAndReleased: "status-completedandreleased",


  NotApplicable1: "status-Drafting-Released",
};

const priorityColorMap: Record<string, string> = {
  Low: 'priority-low',
  Medium: 'priority-medium',
  High: 'priority-high',
  Critical: 'priority-critical',
};
//#region condition
const TeamLeadProjectsWithTasks: React.FC = () => {
  const { role, userID, department } = useAuth();
  const [projects, setProjects] = useState<ProjectDTO[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  const [showSubtasksForTask, setShowSubtasksForTask] = useState<Record<number, boolean>>({});
  const [inlineEditTaskId, setInlineEditTaskId] = useState<number | null>(null);
  const [inlineEditSubTaskId, setInlineEditSubTaskId] = useState<number | null>(null);
  const [inlineNewTaskProjectId, setInlineNewTaskProjectId] = useState<number | null>(null);
  const [inlineNewSubtaskTaskId, setInlineNewSubtaskTaskId] = useState<Record<number, boolean>>({});
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRemarks, setSelectedRemarks] = useState('');
  const [showInlineCreateTask, setShowInlineCreateTask] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [showCompletedReleased, setShowCompletedReleased] = useState(true);
  const [showNotInScope, setshowNotInScope] = useState(false);
  const [useWeekdays, setUseWeekdays] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });
  const [selectedLogEntity, setSelectedLogEntity] = useState<{ type: "task" | "subtask" | "child-subtasks"; id: number; title?: string; } | null>(null);
  const [overallLogs, setOverallLogs] = useState<OverallLogDTO[]>([]);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [totalCommentCount, setTotalCommentCount] = useState<{ [key: string]: number }>({});
  const [unreadCounts, setUnreadCounts] = useState<{ [key: string]: number }>({});
  // 🔥 Spent Hours Modal
  const [spentModalOpen, setSpentModalOpen] = useState(false);
  const [spentLogs, setSpentLogs] = useState<OverallLogDTO[]>([]);
  const [spentEntityTitle, setSpentEntityTitle] = useState("");
  const [childTasks, setChildTasks] = useState<{ [subTaskId: number]: ChildSubTaskViewDTO[]; }>({});
  const [expandedSubTasks, setExpandedSubTasks] = useState<Record<number, boolean>>({});
  const [loadingChildTasks, setLoadingChildTasks] = useState<number[]>([]);
  const [inlineNewChildSubTaskId, setInlineNewChildSubTaskId] =
    useState<Record<number, boolean>>({});
  const [editingChildId, setEditingChildId] = useState<number | null>(null);

  const [exportStartDate, setExportStartDate] = useState<string>("");
  const [exportEndDate, setExportEndDate] = useState<string>("");
  const [exportLoading, setExportLoading] = useState(false);




  const DEFAULT_DUE_COLORS = {
    greaterThan30: "#C8C8C8",   // rgb(200,200,200)
    lessThan30: "#646464",      // rgb(100,100,100)
    lessThan10: "#000000",
    lessThan5: "#0061ff",
    overdue: "#f9001a",
    onTime: "#669c35",
    completed: "#ffffff",
  };


  const [dueColorSettings, setDueColorSettings] = useState(() => {
    const saved = localStorage.getItem("dueColorSettings");
    return saved ? JSON.parse(saved) : DEFAULT_DUE_COLORS;
  });


  const getDueColor = (diff: number, isCompleted: boolean) => {
    if (isCompleted) return dueColorSettings.completed;

    if (diff < 0) return dueColorSettings.overdue;
    if (diff <= 5) return dueColorSettings.lessThan5;
    if (diff <= 10) return dueColorSettings.lessThan10;
    if (diff < 30) return dueColorSettings.lessThan30;

    return dueColorSettings.greaterThan30; // ✅ fixed
  };


  //#region spenthours

  const loadSpentHoursLogs = async (
    type: "task" | "subtask" | "childSubTask",
    id: number,
    title?: string
  ) => {
    try {
      let logs: OverallLogDTO[] = [];

      if (type === "task") {
        logs = await getTaskLogs(id);
      } else if (type === "subtask") {
        logs = await getSubTaskLogs(id);
      } else if (type === "childSubTask") {
        logs = await getChildSubTaskLogs(id);
      }

      // 🔥 ONLY SpentHours changes
      const filtered = logs.filter(
        (l) => l.fieldName === "SpentHours"
      );

      setSpentLogs(filtered);
      setSpentEntityTitle(title || "");
      setSpentModalOpen(true);

    } catch (err) {
      console.error("Spent hours log error", err);
      setSpentLogs([]);
      setSpentModalOpen(true);
    }
  };

  const userHourMap = spentLogs.reduce<Record<string, number>>(
    (acc, log) => {
      const oldVal = Number(log.oldValue ?? 0);
      const newVal = Number(log.newValue ?? 0);
      const diff = newVal - oldVal;

      if (!acc[log.userName]) acc[log.userName] = 0;
      acc[log.userName] += diff;

      return acc;
    },
    {}
  );

  const totalWorked = Object.values(userHourMap).reduce(
    (a, b) => a + b,
    0
  );








  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const [settingsOpen, setSettingsOpen] = useState(false);

  //#region columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "number",
    "description",
    "assigned",
    "status",
    "dueDate",
    "completion",
    "priority",
    "percent",
    "comment", "log", "hours"
  ]);




  const [columnsOrder, setColumnsOrder] = React.useState([
    { key: "number", label: "#", width: "4%" },
    { key: "description", label: "Description", width: "20%" },
    { key: "maxHours", label: "Max", width: "4%" },
    { key: "spentHours", label: "Spent", width: "4%" },
    { key: "hours", label: "Time ", width: "5%" },
    { key: "percent", label: "Work Done ", width: "5%" },
    { key: "assigned", label: "Assigned", width: "7%" },
    { key: "status", label: "Status", width: "8%" },
    { key: "priority", label: "Priority", width: "5%" },
    { key: "dueDate", label: "Due Date", width: "5%" },
    { key: "completion", label: "Due", width: "5%" },
    { key: "comment", label: "Chat", width: "3%" },// NEW Comment column%
    { key: "log", label: "Log", width: "5%" },// NEW Comment column
  ]);

  //#region status

  const allStatuses = [
    "YetToStart",
    "WaitingForInput",
    "Cancelled",
    "OnHold",
    "Issuse",
    "Released",
    "ModellingTodo",
    "DraftingTodo",
    "QCTodo",
    "RevisionTodo",
    "ClientReviewTodo",
    "FinalCorrectionTodo",
    "ModellingInProgress",
    "DraftingInProgress",
    "QCInProgress",
    "RevisionInProgress",
    "ClientReviewInProgress",
    "FinalCorrectionInProgress",
    "ModellingCompleted",
    "DraftingCompleted",
    "QCCompleted",
    "RevisionCompleted",
    "ClientReviewCompleted",
    "FinalCorrectionCompleted",
    // "Completed",
  ];

  const inProgressStatuses = [
    "ModellingInProgress",
    "DraftingInProgress",
    "QCInProgress",
    "RevisionInProgress",
    "ClientReviewInProgress",
    "FinalCorrectionInProgress"
  ];

  const completedStatuses = [
    "ModellingCompleted",
    "DraftingCompleted",
    "QCCompleted",
    "RevisionCompleted",
    "ClientReviewCompleted",
    "FinalCorrectionCompleted",
  ];

  const ToDoStatuses = [
    "ModellingTodo",
    "DraftingTodo",
    "QCTodo",
    "RevisionTodo",
    "ClientReviewTodo",
    "FinalCorrectionTodo"
  ];

  //#region settings

  useEffect(() => {
    // const savedColumns = localStorage.getItem("visibleColumns");
    // if (savedColumns) setVisibleColumns(JSON.parse(savedColumns));

    const saved = localStorage.getItem("showCompletedReleased");
    if (saved) setShowCompletedReleased(saved === "true");

    const savednot = localStorage.getItem("showNotInScope");
    if (savednot) setshowNotInScope(savednot === "true");

    const savedWeekdays = localStorage.getItem("useWeekdays");
    if (savedWeekdays) setUseWeekdays(savedWeekdays === "true");

    const savedOnDays = localStorage.getItem("showOnDays");
    if (savedOnDays) setShowOnDays(savedOnDays === "false");
  }, []);

  useEffect(() => {
    const savedUnread = localStorage.getItem("unreadCounts");
    if (savedUnread) setUnreadCounts(JSON.parse(savedUnread));
  }, []); // Only once!


  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{
    type: "task" | "subtask";
    id: number;
  } | null>(null);

  const entity = selectedEntity;


  //#region comments

  const [commentPopup, setCommentPopup] = useState<{
    open: boolean;
    type: "project" | "task" | "subtask";
    id: number;
    title?: string;
  } | null>(null);

  const openComments = (
    type: "project" | "task" | "subtask",
    id: number,
    title?: string
  ) => {
    setCommentPopup({
      open: true,
      type,
      id,
      title
    });
  };

  const loadTotalCommentsFor = async (type: "task" | "subtask", id: number) => {
    try {
      const comments = await getComments(type, id);
      setTotalCommentCount(prev => {
        const updated = { ...prev, [`${type}-${id}`]: comments.length };
        localStorage.setItem("totalCommentCount", JSON.stringify(updated)); // 🔥 Save permanently
        return updated;
      });
    } catch {
      setTotalCommentCount(prev => {
        const updated = { ...prev, [`${type}-${id}`]: 0 };
        localStorage.setItem("totalCommentCount", JSON.stringify(updated));
        return updated;
      });
    }
  };

  const loadUnreadCountFor = async (type: "task" | "subtask", id: number) => {
    try {
      const key = `${type}-${id}`;

      // 🔥 If unread exists already in localStorage, do NOT override it from API
      if (unreadCounts[key] > 0) return;

      const count = await getUnreadCount(type, id);

      setUnreadCounts(prev => {
        const updated = { ...prev, [key]: count };
        localStorage.setItem("unreadCounts", JSON.stringify(updated));
        return updated;
      });

    } catch {
      setUnreadCounts(prev => {
        const updated = { ...prev, [`${type}-${id}`]: prev[`${type}-${id}`] ?? 0 };
        localStorage.setItem("unreadCounts", JSON.stringify(updated));
        return updated;
      });
    }
  };





  //#region Draggable Modal Logic 
  const handleMouseDown = (e: any) => {
    setDragging(true);
    setOffset({
      x: e.clientX - modalPos.x,
      y: e.clientY - modalPos.y,
    });
  };

  const handleMouseMove = (e: any) => {
    if (dragging) {
      setModalPos({
        x: e.clientX - offset.x,
        y: e.clientY - offset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, offset, modalPos]);

  const dragColIndex = useRef<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);


  //#region overall log

  const loadOverallLogs = async (
    entityType: "task" | "subtask" | "childSubTask",
    entityId: number
  ) => {
    try {
      let logs: OverallLogDTO[] = [];

      if (entityType === "task") {
        logs = await getTaskLogs(entityId);
      } else if (entityType === "subtask") {
        logs = await getSubTaskLogs(entityId);
      } else if (entityType === "childSubTask") {
        logs = await getChildSubTaskLogs(entityId);
      }

      setOverallLogs(logs);
      setLogModalOpen(true);

    } catch (err) {
      console.error("Failed to load overall logs:", err);
      setOverallLogs([]);
    }
  };


  //#region C.D, W.D days

  const [showOnDays, setShowOnDays] = useState(true);

  const toDisplayValue = (hours: number) =>
    showOnDays
      ? Math.round(hours)
      : Math.round(hours / 9);

  const unitLabel = showOnDays ? " H" : "  ";

  const countWeekdaysSigned = (start: Date, end: Date) => {
    const a = new Date(start);
    const b = new Date(end);
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);

    if (a.getTime() === b.getTime()) return 0;

    const forward = b > a;
    let d = new Date(a);
    let count = 0;

    d.setDate(d.getDate() + (forward ? 1 : -1));

    while (forward ? d <= b : d >= b) {
      const day = d.getDay(); // 0=Sun, 6=Sat
      if (day !== 0 && day !== 6) {
        count += forward ? 1 : -1;
      }
      d.setDate(d.getDate() + (forward ? 1 : -1));
    }

    return count;
  };

  type ColumnDef = {
    key: string;
    label: string;
    width: string;
  };

  const visibleOrderedColumns: ColumnDef[] = columnsOrder.filter(
    (col: ColumnDef) => visibleColumns.includes(col.key)
  );






  const formatStatusLabel = (status: string) => {
  const overrides: Record<string, string> = {
    Completed: "Ready for Release",
    YetToStart: "Yet to Start",
    NotApplicable: "Not In Scope",
  };

  if (overrides[status]) {
    return overrides[status];
  }

  return status
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/And/g, "&")
    .trim();
};


const childformatStatusLabel = (status: string) => {
  const overrides1: Record<string, string> = {
    Completed: "Completed",
    YetToStart: "Yet to Start",
    NotApplicable: "Not In Scope",
  };

  if (overrides1[status]) {
    return overrides1[status];
  }

  return status
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/And/g, "&")
    .trim();
};



  //#region Task, subtask

  const toggle = () => setDropdownOpen((prev) => !prev);

  useEffect(() => {
    if (role !== 'TeamLeader' && role !== 'ProjectManager' && role !== 'Admin') return;

    (async () => {
      await fetchProjects();
      await fetchTeamMembers();
    })();
  }, [role]);

  async function fetchTeamMembers() {
    try {
      const res = await getTeammember();
      const data = res.data?.$values || res.data || [];
      setTeamMembers(data);
    } catch { }
  }
  async function fetchProjects() {
    setLoadingProjects(true);
    setProjectError(null);

    try {
      let res;

      if (role === "TeamLeader") {
        res = await getTeamLeadProjects();
      } else if (role === "ProjectManager") {
        res = await getMyProjects();
      } else if (role === "Admin") {
        res = await getAllProjects();   // 👈 Admin access added
      } else {
        return [];
      }

      const data = res.data?.$values || res.data || [];
      const projectsList = data.map((p: any) => ({ ...p, id: Number(p.id) }));
      setProjects(projectsList);

      return projectsList;
    }
    catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to load projects";
      setProjectError(message);
      toast.error(message);
      return [];
    } finally {
      setLoadingProjects(false);
    }
  }
  async function fetchProjectTasks(projectId: number) {
    setLoadingTasks(true);
    setTasksError(null);

    try {
      // 🔎 Find selected project
      const project = projects.find(p => p.id === projectId);
      if (!project) throw new Error("Project not found");

      // 🔐 Role-based API call
      let res;
      if (role === "TeamLeader") {
        res = await getTLProjectDetails(project.projectID);
      } else if (role === "Admin" || role === "ProjectManager") {
        res = await getProjectDetails(project.projectID);
      } else {
        throw new Error("Unauthorized role");
      }

      if (!res?.data) throw new Error("No data received from API");

      // 🧠 Normalize tasks array
      const tasksArray =
        Array.isArray(res.data.tasks)
          ? res.data.tasks
          : res.data.tasks?.$values || [];

      const tasksData: TaskDTO[] = tasksArray.map((task: any) => {
        const rawSubTasks =
          Array.isArray(task.subTasks)
            ? task.subTasks
            : task.subTasks?.$values || [];

        return {
          ...task,

          // ✅ Normalize task fields
          description: task.description || task.title || "",
          issues: task.issues || "",
          spentHours: task.spentHours ?? task.SpentHours ?? 0,
          maxHours: task.maxHours ?? task.MaxHours ?? 0,
          percentCompleted: task.percentCompleted ?? 0,

          createdByName:
            task.createdByName ||
            task.createdBy?.name ||
            task.createdByUserName ||
            task.createdByFullName ||
            "",

          totalLogs: Array.isArray(task.logs)
            ? task.logs.length
            : task.logs?.$values?.length ?? 0,

          // ✅ Normalize subtasks
          subTasks: rawSubTasks.map((sub: any) => ({
            ...sub,
            taskId: task.id,
            parentSubTaskId: sub.parentSubTaskId ?? null,

            description: sub.description || sub.title || "",
            issues: sub.issues || "",
            priority: sub.priority || "Medium",
            percentCompleted: sub.percentCompleted ?? 0,

            spentHours: sub.spentHours ?? sub.SpentHours ?? 0,
            maxHours: sub.maxHours ?? sub.MaxHours ?? 0,

            assignedToId: sub.assignedToId || sub.assigned || "",

            createdByName:
              sub.createdByName ||
              sub.createdBy?.name ||
              sub.createdByUserName ||
              sub.createdByFullName ||
              task.createdByName ||
              "",

            totalLogs: Array.isArray(sub.logs)
              ? sub.logs.length
              : sub.logs?.$values?.length ?? 0,
          })),
        };
      });

      // 🧩 Attach tasks to project
      setProjects(prev =>
        prev.map(p =>
          p.id === projectId ? { ...p, tasks: tasksData } : p
        )
      );

      // 🔔 Load unread counts & comments
      await Promise.all([
        ...tasksData.map(t => loadUnreadCountFor("task", t.id)),
        ...tasksData.map(t => loadTotalCommentsFor("task", t.id)),
        ...tasksData.flatMap(t =>
          t.subTasks.map(st => loadUnreadCountFor("subtask", st.id))
        ),
        ...tasksData.flatMap(t =>
          t.subTasks.map(st => loadTotalCommentsFor("subtask", st.id))
        )
      ]);

      // ✅ PRELOAD CHILD TASK HOURS
      // await preloadAllChildSubtasks(tasksData);



      // 🔄 UI resets
      setShowSubtasksForTask({});
      setInlineNewTaskProjectId(projectId);

    } catch (error: any) {
      console.error("Error fetching project details:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Error loading tasks for this project";
      setTasksError(message);
      toast.error(message);
    } finally {
      setLoadingTasks(false);
    }
  }

  function toggleSubtasksDropdown(taskId: number) {
    setShowSubtasksForTask(prev => {
      const isCurrentlyOpen = prev[taskId];
      if (isCurrentlyOpen) {
        return {};
      }
      return { [taskId]: true };
    });

    setInlineNewSubtaskTaskId(prevInline => ({
      ...prevInline,
      [taskId]: false,
    }));
  }
  async function refreshTasksList() {
    if (!selectedProjectId) return;
    await fetchProjectTasks(selectedProjectId);
  }
  function openInlineEditTask(taskId: number) {
    setInlineEditTaskId(taskId);
    setInlineEditSubTaskId(null);
    setInlineNewSubtaskTaskId({});
  }
  function closeInlineEditTask(refresh = false) {
    setInlineEditTaskId(null);
    if (refresh) {
      refreshTasksList();
    }
  }
  function openInlineEditSubTask(subTaskId: number, taskId: number) {
    setInlineEditSubTaskId(subTaskId);
    setInlineEditTaskId(null);
    setInlineNewSubtaskTaskId({});
    setShowSubtasksForTask(prev => ({ ...prev, [taskId]: true }));
  }
  async function closeInlineEditSubTask(refresh = false, taskId?: number) {
    setInlineEditSubTaskId(null);

    if (refresh && taskId) {
      await refreshTasksList();
      setShowSubtasksForTask({ [taskId]: true });
    }
  }
  function openInlineCreateSubTask(taskOrSubtaskId: number) {
    setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: true }));
    setInlineEditTaskId(null);
    setInlineEditSubTaskId(null);
    setShowSubtasksForTask(prev => ({ ...prev, [taskOrSubtaskId]: true }));
  }
  async function closeInlineCreateSubTask(taskOrSubtaskId: number, refresh = false) {
    setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: false }));
    if (refresh) {
      refreshTasksList();
      setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: true }));
    }
  }
  const handleViewMore = (remarks: string) => {
    setSelectedRemarks(remarks);
    setRemarksModalOpen(true);
  };
  const openInlineCreateTask = () => setShowInlineCreateTask(true);
  const closeInlineCreateTask = () => setShowInlineCreateTask(false);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleProjectChange = async (event: React.ChangeEvent<any>) => {
    const selectedValue = event.target.value;

    setInlineEditTaskId(null);
    setInlineEditSubTaskId(null);

    if (selectedValue === 'select' || selectedValue === '') {
      setSelectedProjectId(null);
      setProjects(prev => prev.map(p => ({ ...p, tasks: [] })));
      setTasksError(null);
      return;
    }

    const projectId = parseInt(selectedValue, 10);
    if (!isNaN(projectId)) {
      setSelectedProjectId(projectId);
      await fetchProjectTasks(projectId);
    }
  };

  if (role !== 'TeamLeader' && role !== 'ProjectManager' && role !== 'Admin') {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Alert color="danger">Access denied</Alert>
      </Container>
    );
  }


  const getDueDateColor = (dateString: string | null | undefined, status: string): string => {
    if (!dateString) return 'secondary';
    if (status === 'Completed' || status === 'Released') return 'success';

    const dueDate = new Date(dateString);
    const today = new Date();
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'danger';
    }

    if (diffDays <= 2) {
      return 'warning';
    }

    return 'success';
  };
  const getDueDateMessage = (dateString: string | null | undefined, status: string): string | null => {
    if (!dateString || status === 'Completed' || status === 'Released') return null;
    const dueDate = new Date(dateString);
    const today = new Date();
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `This project is overdue by ${Math.abs(diffDays)} day(s).`;
    }
    if (diffDays === 0) {
      return "This project is due today!";
    }
    if (diffDays <= 2) {
      return `This project is due in ${diffDays} day(s).`;
    }

    return null;
  };
  const getProjectOverallStatus = (tasks: TaskDTO[]): string => {
    const finishedStatuses = ['Completed', 'Released'];

    if (!tasks || tasks.length === 0) {
      return 'InProgress';
    }

    const allItemsAreFinished = tasks.every(task => {
      const taskIsFinished = finishedStatuses.includes(task.status);
      const subtasksAreFinished = (task.subTasks ?? []).every(subtask =>
        finishedStatuses.includes(subtask.status)
      );
      return taskIsFinished && subtasksAreFinished;
    });

    return allItemsAreFinished ? 'Completed' : 'InProgress';
  };

  // === Filtering and task list preparation ===
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  const allTasksForProject = selectedProject?.tasks || [];

  const tasksToShowBasedOnToggle = allTasksForProject;



  const projectStatus = getProjectOverallStatus(allTasksForProject);

  const handleWarningIconClick = () => {
    if (!selectedProject) return;
    const message = getDueDateMessage(selectedProject.endDate, projectStatus);
    if (message) {
      toast.warning(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  const getWidth = (text: any) => {
    const baseWidth = 100;
    const incrementPerChar = 8;
    return baseWidth + text.length * incrementPerChar;
  };

  const handleDragStart = (e: React.DragEvent<HTMLTableHeaderCellElement>, index: number) => {
    dragColIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableHeaderCellElement>) => {
    e.preventDefault();
  };

  const handleDrop = (
    e: React.DragEvent<HTMLTableHeaderCellElement>,
    dropIndex: number
  ) => {
    e.preventDefault();
    if (dragColIndex.current === null) return;

    const visibleKeys = visibleOrderedColumns.map(
      (c: ColumnDef) => c.key
    );

    const fromKey = visibleKeys[dragColIndex.current];
    const toKey = visibleKeys[dropIndex];

    const fromIndex = columnsOrder.findIndex(c => c.key === fromKey);
    const toIndex = columnsOrder.findIndex(c => c.key === toKey);

    if (fromIndex === -1 || toIndex === -1) return;

    const updated = [...columnsOrder];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);

    setColumnsOrder(updated);
    dragColIndex.current = null;
  };




  // -----------------------------------------------------
  // 6️⃣ STATUS MODAL MULTI-SELECT FILTER
  // -----------------------------------------------------
  // 1️⃣ Start with all tasks
  let filteredTasks = allTasksForProject.slice();

  // 2️⃣ Apply ONLY Status Multi-Select Filter
  if (selectedStatuses.length > 0) {
    filteredTasks = filteredTasks
      .map(task => {
        const taskMatches = selectedStatuses.includes(task.status);

        const matchingSubtasks = (task.subTasks || []).filter(st =>
          selectedStatuses.includes(st.status)
        );

        if (taskMatches || matchingSubtasks.length > 0) {
          return {
            ...task,
            subTasks: taskMatches ? task.subTasks : matchingSubtasks
          };
        }

        return null;
      })
      .filter(Boolean) as TaskDTO[];
  }

  // Hide Completed / Released
  if (!showCompletedReleased) {
    filteredTasks = filteredTasks
      .filter(task =>
        task.status !== "Completed" &&
        task.status !== "Released"
      )
      .map(task => ({
        ...task,
        subTasks: (task.subTasks || []).filter(st =>
          st.status !== "Completed" &&
          st.status !== "Released"
        )
      }));
  }

  // Hide Not In Scope
  if (!showNotInScope) {
    filteredTasks = filteredTasks
      .filter(task => task.status !== "NotInScope")
      .map(task => ({
        ...task,
        subTasks: (task.subTasks || []).filter(st =>
          st.status !== "NotInScope"
        )
      }));
  }



  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };




  //#region Expand childsubtask

  const expandSubTask = async (subTaskId: number) => {
    if (!["Admin", "TeamLeader", "ProjectManager"].includes(role)) {
      toast.error("Access denied");
      return;
    }

    setExpandedSubTasks(prev => ({ ...prev, [subTaskId]: true }));

    setExpandedSubTasks(prev => ({ ...prev, [subTaskId]: true }));

    if (childTasks[subTaskId]) return;

    const res = await getChildSubTasksBySubTask(subTaskId);
    setChildTasks(prev => ({ ...prev, [subTaskId]: res.data }));
  };


  const collapseSubTask = (subTaskId: number) => {
    setExpandedSubTasks(prev => ({ ...prev, [subTaskId]: false }));
  };


  const calculatePercent = (spent?: number, max?: number) => {
    if (!max || max === 0) return 0;
    return Math.min(100, Math.round(((spent ?? 0) / max) * 100));
  };


  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };


  const UserAvatar = ({ name }: { name: string }) => {
    const initials = getInitials(name);

    return (
      <div
        title={name}
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "#008BFF",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 600,
          marginRight: 4,
          cursor: "default",
        }}
      >
        {initials}
      </div>
    );
  };


  const getMaxSubtaskDate = (subTasks: any[]) => {
    if (!subTasks || subTasks.length === 0) return null;

    const validDates = subTasks
      .map(s => s.dueDate ? new Date(s.dueDate) : null)
      .filter((d): d is Date => d !== null);

    if (validDates.length === 0) return null;

    const maxDate = new Date(
      Math.max(...validDates.map(d => d.getTime()))
    );

    return maxDate.toISOString(); // ✅ return string
  };


  const calculateDiff = (start: Date, end: Date) =>
    useWeekdays
      ? countWeekdaysSigned(start, end)
      : Math.round(
        (end.getTime() - start.getTime()) /
        (1000 * 60 * 60 * 24)
      );



      




  // --- Render Sorted Tasks and Subtasks ---
  // Sorting code explained: Completed tasks/subtasks always last,
  // original order for others unchanged.

  return (
    <Container fluid>
      <Row className=" mb-1 d-flex justify-content-between align-items-center" style={{ backgroundColor: "#dfe2e4ff", height: "60px", borderRadius: "10px" }}>
        <Col sm={12} md={6} lg={4}>
          <Dropdown className='drop-select shadow-sm rounded-3' isOpen={dropdownOpen} toggle={toggle} style={{ width: 'max-content' }} >
            <DropdownToggle caret color="white" className="w-100 text-start fw-semibold border rounded-3 py-2 px-3 d-flex justify-content-between align-items-center">
              {selectedProject
                ? `${selectedProject.projectID} - ${selectedProject.projectName}`
                : 'Select a project'}
            </DropdownToggle>

            <DropdownMenu className="custom-dropdown-menu shadow rounded-3 p-2">
              {projects.map((p) => (
                <DropdownItem
                  key={p.id}
                  onClick={() =>
                    handleProjectChange({
                      target: { value: String(p.id) }
                    } as React.ChangeEvent<HTMLInputElement>)}
                  className="custom-dropdown-item rounded-2 py-2"
                >
                  <span className="custom-dropdown-item rounded-2 py-2">
                    {p.projectID} - {p.projectName}
                  </span>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </Col>
        {selectedProject && (
          <Col lg={8} md={6} sm={12} className="project-details">

            <div className="d-flex flex-wrap align-items-center gap-2">



              {/* Filters */}
              <Button color="light" onClick={() => setStatusModalOpen(true)}>
                Status Filter
              </Button>

              <Button color="light" onClick={() => setSelectedStatuses([])}>
                Clear
              </Button>

              <Button color="light" onClick={() => setSettingsOpen(true)}>
                <Settings />
              </Button>

              {/* Warning Icon */}
              {(getDueDateColor(selectedProject.endDate, projectStatus) === 'warning' ||
                getDueDateColor(selectedProject.endDate, projectStatus) === 'danger') && (
                  <WarningAmberIcon
                    fontSize="large"
                    className="blinking-warning"
                    style={{ color: '#ffbf00ff', cursor: 'pointer' }}
                    onClick={handleWarningIconClick}
                  />
                )}
              <AdminTaskSettings />

              {/* Schedule aligned right */}
              {/* <div className="ms-auto d-flex align-items-center gap-2 fw-bold">
      <span>Project Schedule:</span>
      {formatDate(selectedProject.startDate)}
      <span>-</span>
      {formatDate(selectedProject.endDate)}
    </div> */}

            </div>

          </Col>

        )}
      </Row>
      <Card className="shadow-sm border-0 rounded-3">

        {statusModalOpen && (
          <Card>
            <CardHeader>
              {/* <div className="fw-bold mb-2">Filter by Status</div> */}

              <Row className="row-cols-1 row-cols-md-4 g-2 fw-semibold align-items-center">

                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={selectedStatuses.length === allStatuses.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedStatuses(allStatuses);
                      else setSelectedStatuses([]);
                    }}
                  />
                  <span>Select All</span>
                </Col>



                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={inProgressStatuses.every(s => selectedStatuses.includes(s))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...inProgressStatuses])));
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => !inProgressStatuses.includes(s)));
                      }
                    }}
                  />
                  <span>Select All In-Progress</span>
                </Col>
                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={ToDoStatuses.every(s => selectedStatuses.includes(s))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...ToDoStatuses])));
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => !ToDoStatuses.includes(s)));
                      }
                    }}
                  />
                  <span>Select All ToDo</span>
                </Col>

                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={completedStatuses.every(s => selectedStatuses.includes(s))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...completedStatuses])));
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => !completedStatuses.includes(s)));
                      }
                    }}
                  />
                  <span>Select All Completed</span>
                  <Button style={{ marginLeft: "200px" }} close onClick={() => setStatusModalOpen(false)} />
                </Col>


              </Row>
            </CardHeader>


            <CardBody style={{ padding: "20px" }}>

              {/* --------- Master Controls --------- */}


              {/* --------- Status List --------- */}
              <Row className="row-cols-6">
                {allStatuses.map((status) => (
                  <Col key={status} className="mb-2">
                    <div className="d-flex align-items-center">
                      <Input
                        id={`status-${status}`}
                        type="checkbox"
                        checked={selectedStatuses.includes(status)}
                        onChange={() => {
                          selectedStatuses.includes(status)
                            ? setSelectedStatuses(selectedStatuses.filter(s => s !== status))
                            : setSelectedStatuses([...selectedStatuses, status]);
                        }}
                      />
                      <Label check htmlFor={`status-${status}`} className="ms-2" style={{ cursor: "pointer" }}>
                        {status}
                      </Label>
                    </div>
                  </Col>
                ))}
              </Row>


            </CardBody>
          </Card>
        )}

        <CardBody>


          {selectedProjectId === null && <Alert color="info" className="text-center">Please select a project to view its tasks.</Alert>}
          {loadingTasks && <div className="text-center p-4"><Spinner /> <p>Loading tasks...</p></div>}
          {tasksError && <Alert color="danger">{tasksError}</Alert>}
          {!loadingTasks && selectedProject && filteredTasks.length === 0 && (
            <Alert color="info" className="text-center">
              {searchTerm ? `No tasks found for "${searchTerm}".` : "No tasks found for this project."}
            </Alert>
          )}

          {!loadingTasks && selectedProject && (
            <div className="task-table-wrapper">
              <Table responsive size="sm" style={{ tableLayout: 'fixed', minWidth: "600px" }}  >
                <thead style={{ backgroundImage: 'linear-gradient(to right, #004080, #0059b3)', color: 'white' }}>
                  <tr>
                    {visibleOrderedColumns.map((col, index) => (
                      <th
                        key={col.key}
                        style={{ width: col.width, cursor: "move", userSelect: "none" }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        onClick={() => handleSort?.(col.key)} // optional if sort exists
                      >
                        {col.key === "completion" ? (
                          <td
                            onClick={(e) => {
                              e.stopPropagation(); // 🔥 prevent sort / drag
                              setUseWeekdays(prev => {
                                const v = !prev;
                                localStorage.setItem("useWeekdays", String(v));
                                return v;
                              });
                            }}
                            title={
                              useWeekdays
                                ? "Switch to Calendar Days (Mon–Sun)"
                                : "Switch to Working Days (Mon–Fri)"
                            }
                          >
                            Due {useWeekdays ? "(WD)" : "(CD)"}
                          </td>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {[...filteredTasks]
                    .sort((a, b) => {
                      if (a.status === 'Completed' && b.status !== 'Completed') return 1;
                      if (b.status === 'Completed' && a.status !== 'Completed') return -1;
                      return 0;
                    })
                    .map((task) => (
                      <React.Fragment key={task.id}>

                        {/* Inline Edit Task Row */}

                        {/* ==== Main Task Row ==== */}
                        <tr style={{ backgroundColor: '#d8d8dbff' }}>
                          {visibleOrderedColumns.map((col) => {
                            switch (col.key) {
                              case "number":
                                return (
                                  <td key={col.key} className="align-middle text-center" >
                                    {task.subTasks && (
                                      <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => toggleSubtasksDropdown(task.id)}
                                        style={{ padding: 0 }}
                                      >
                                        {task.subTasks && task.subTasks.length > 0 && (
                                          showSubtasksForTask[task.id] ? (
                                            <ExpandLessIcon />
                                          ) : (
                                            <ExpandMoreIcon />
                                          )
                                        )}

                                      </Button>
                                    )}
                                  </td>
                                );
                              case "description":
                                return (
                                  <td key={col.key} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} data-label="Description">
                                    {task.description}
                                  </td>
                                );
                              case "spentHours": {
                                const spent = task.totalSpent ?? 0;

                                return (
                                  <td>
                                    {toDisplayValue(spent)}{unitLabel}
                                  </td>
                                );
                              }

                              case "maxHours": {
                                const max = task.totalMax ?? 0;
                                return <td>{toDisplayValue(max)}{unitLabel}</td>;
                              }


                              case "Remaining": {
                                const spent = task.totalSpent ?? 0;
                                const max = task.totalMax ?? 0;

                                const remaining = max - spent;

                                return (
                                  <td style={{ color: remaining < 0 ? "red" : "inherit" }}>
                                    {toDisplayValue(remaining)}{unitLabel}
                                  </td>
                                );
                              }

                              case "hours": {

                                const spent = task.totalSpent ?? 0;
                                const max = task.totalMax ?? 0;
                                const remaining = max - spent;

                                const hoursPercent =
                                  max > 0
                                    ? Math.min(100, Math.round((spent / max) * 100))
                                    : 0;

                                const isMatch = hoursPercent === task.percentCompleted;

                                return (
                                  <td key={col.key} style={{ minWidth: "120px" }}>
                                    <Tooltip
                                      arrow
                                      title={
                                        <>
                                          <div>Max: {toDisplayValue(max)}{unitLabel}</div>
                                          <div>Spent: {toDisplayValue(spent)}{unitLabel}</div>
                                          <div>Rem: {toDisplayValue(remaining)}{unitLabel}</div>
                                        </>
                                      }
                                    >
                                      <div className="d-flex flex-column gap-1">
                                        <Progress
                                          value={hoursPercent}
                                          max={100}
                                          color={isMatch ? "primary" : "danger"}
                                          style={{ cursor: "pointer" }}
                                        >
                                          <strong>{hoursPercent}%</strong>
                                        </Progress>
                                      </div>
                                    </Tooltip>
                                  </td>
                                );
                              }



                              case "assigned":
                                return (
                                  <td key={col.key} data-label="Assigned">
                                    {teamMembers.find(m => m.userID === task.assignedToId)?.name || '—'}
                                  </td>
                                );
                              // case "assignedby":
                              //   return (
                              //     <td key={col.key} data-label="Assigned By">
                              //       {teamMembers.find(m => m.userID === task.createdById)?.name
                              //         || task.createdByName
                              //         || '—'}
                              //     </td>
                              //   );


                              case "status":
                                return (
                                  <td key={col.key} data-label="Status">
                                    <Badge className={statusColorMap[task.status] || 'status-pending'}>
                                      {formatStatusLabel(task.status)}
                                    </Badge>
                                  </td>
                                );
                              case "priority":
                                return (
                                  <td key={col.key} data-label="Priority">
                                    <Badge className={priorityColorMap[task.priority] || 'priority-medium'}>
                                      {task.priority}
                                    </Badge>
                                  </td>
                                );
                              case "dueDate": {
                                const maxDate = getMaxSubtaskDate(task.subTasks);


                                return (
                                  <td key={col.key} data-label="Due Date">
                                    {maxDate ? formatDate(maxDate) : formatDate(task.dueDate)}

                                    <span
                                      className="text-black ms-1"
                                      title={task.isDueDateLocked ? "Due date is locked by admin" : ""}
                                    >
                                      {task.isDueDateLocked && <HttpsIcon fontSize="small" />}
                                    </span>
                                  </td>
                                );
                              }

                              case "completion": {
                                if (!task.subTasks || task.subTasks.length === 0)
                                  return <td>—</td>;

                                const maxDateString = getMaxSubtaskDate(task.subTasks);
                                if (!maxDateString) return <td>—</td>;

                                const due = new Date(maxDateString);
                                const today = new Date();
                                const completed = task.completionDate
                                  ? new Date(task.completionDate)
                                  : null;

                                const status = task.status?.toLowerCase();

                                // normalize time
                                due.setHours(0, 0, 0, 0);
                                today.setHours(0, 0, 0, 0);
                                completed?.setHours(0, 0, 0, 0);

                                const isCompleted =
                                  status === "released" && completed !== null;

                                let diff = 0;
                                let tooltipText = "";
                                let displayText = "";

                                // ✅ COMPLETED TASK
                                if (isCompleted) {
                                  diff = calculateDiff(due, completed!);

                                  if (diff === 0) {
                                    tooltipText = "Completed On Time";
                                  } else if (diff < 0) {
                                    tooltipText = `Completed Early by ${Math.abs(diff)} days`;
                                  } else {
                                    tooltipText = `Completed Late by ${diff} days`;
                                  }

                                  displayText = "Completed";
                                }
                                // ✅ ACTIVE TASK
                                else {
                                  // ✅ IMPORTANT: today first, due second
                                  diff = calculateDiff(today, due);

                                  if (diff === 0) {
                                    displayText = "0 day";
                                    tooltipText = "Due today";
                                  } else if (diff < 0) {
                                    displayText = `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
                                    tooltipText = `Overdue by ${Math.abs(diff)} days`;
                                  } else {
                                    displayText = `${diff} day${diff === 1 ? "" : "s"}`;
                                    tooltipText = `${diff} days remaining`;
                                  }
                                }

                                const color = getDueColor(diff, isCompleted);

                                return (
                                  <td title={tooltipText} style={{ color, fontWeight: 600 }}>
                                    {displayText}
                                  </td>
                                );
                              }



                              case "percent":
                                return (
                                  <td key={col.key} style={{ minWidth: "120px" }}>
                                    <Tooltip
                                      arrow
                                      title={
                                        <>
                                          <div>{task.percentCompleted}% completed</div>
                                        </>
                                      }
                                    >
                                      <div className="d-flex flex-column gap-1">
                                        <Progress
                                          value={task.percentCompleted}
                                          max={100}
                                        >
                                        </Progress>
                                      </div>
                                    </Tooltip>
                                  </td>
                                );



                              case "comment": {
                                const total = totalCommentCount[`task-${task.id}`] ?? 0;
                                const unread = unreadCounts[`task-${task.id}`] ?? 0;

                                return (
                                  <td data-label="Comment">
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="position-relative"
                                      onClick={() => {
                                        openComments("task", task.id, task.description);

                                        setUnreadCounts(prev => {
                                          const updated = { ...prev, [`task-${task.id}`]: 0 };
                                          localStorage.setItem("unreadCounts", JSON.stringify(updated));
                                          return updated;
                                        });
                                      }}

                                    >

                                      {/* 🟦 NO COMMENTS */}
                                      {total === 0 && (
                                        <ChatBubbleOutline fontSize="small" style={{ color: "blue", opacity: 0.6 }} />
                                      )}

                                      {/* 🟢 ALL READ (if comments exist but no unread) */}
                                      {total > 0 && unread === 0 && (
                                        <Chat fontSize="small" style={{ color: "#0084ffff" }} />
                                      )}

                                      {/* 🔴 UNREAD COMMENTS */}
                                      {unread > 0 && (
                                        <>
                                          <MarkChatUnread fontSize="small" style={{ color: "blue" }} />

                                          <Badge
                                            color="danger"
                                            className="position-absolute top-0 start-100 translate-middle badge-pulse"
                                            style={{
                                              background: "#d32f2f",
                                              color: "white",
                                              padding: "3px 6px",
                                              fontSize: "11px",
                                              borderRadius: "16px",
                                              border: "1.5px solid white",
                                              fontWeight: 600,
                                              boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                                            }}
                                          >
                                            {unread}
                                          </Badge>
                                        </>
                                      )}
                                    </Button>
                                    {/* <HelpOutlineIcon
                                                                        style={{
                                                                          width: "50px",
                                                                          cursor: "pointer"
                                                                        }}
                                                                        className="ms-1"
                                                                        onClick={() => {
                                                                          setSelectedEntity({ type: "task", id: task.id });
                                                                          setInputModalOpen(true);
                                                                        }}
                                                                      >
                                                                        <title>
                                                                          {task.isClientInputEnabled
                                                                            ? "Client input disabled"
                                                                            : "Ask / View client input"}
                                                                        </title>
                              
                              
                                                                      </HelpOutlineIcon> */}
                                  </td>
                                );
                              }




                              case "log":
                                return (
                                  <td data-label="Log">
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="p-0"  // 🔹 Removes extra padding, looks like a plain link
                                      onClick={() => {
                                        setSelectedLogEntity({ type: "task", id: task.id, title: task.description });
                                        loadOverallLogs("task", task.id);
                                      }}
                                    >
                                      {formatDate(task.completionDate)}
                                    </Button>
                                  </td>
                                );
                              default:
                                return null;
                            }
                          })}
                        </tr>


                        {/* ==== Subtasks ==== */}
                        {showSubtasksForTask[task.id] &&
                          [...(task.subTasks ?? [])]
                            .sort((a, b) => {
                              if (a.status === 'Completed' && b.status !== 'Completed') return 1;
                              if (b.status === 'Completed' && a.status !== 'Completed') return -1;
                              return 0;
                            })
                            .map((subtask) => (
                              <React.Fragment key={subtask.id}>

                                <tr style={{ backgroundColor: '#fffbe6', borderTop: '1px solid #dee2e6' }}>
                                  {visibleOrderedColumns.map((col) => {
                                    switch (col.key) {

                                      case "number":
                                        return (
                                          <td key={col.key}>

                                            <Button
                                              style={{ marginLeft: "60px" }}
                                              size="sm"
                                              color="link"
                                              onClick={() =>
                                                expandedSubTasks[subtask.id]
                                                  ? collapseSubTask(subtask.id)
                                                  : expandSubTask(subtask.id)
                                              }
                                            >
                                              {expandedSubTasks[subtask.id]
                                                ? <ExpandLessIcon />
                                                : <ExpandMoreIcon />}
                                            </Button>

                                          </td>
                                        );
                                      case "description":
                                        return (
                                          <td data-label="description"
                                            key={col.key}
                                            style={{
                                              paddingLeft: '2rem',
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word',
                                            }}
                                          >
                                            {subtask.description}
                                          </td>
                                        );
                                      case "spentHours": {
                                        const spent = subtask.totalSpent ?? 0;

                                        return (
                                          <td>
                                            {toDisplayValue(spent)}{unitLabel}
                                          </td>
                                        );
                                      }



                                      case "maxHours": {
                                        const max = subtask.totalMax ?? 0;
                                        return <td>{toDisplayValue(max)}{unitLabel}</td>;
                                      }


                                      case "Remaining": {
                                        const spent = subtask.totalSpent ?? 0;
                                        const max = subtask.totalMax ?? 0;

                                        const remaining = max - spent;

                                        return (
                                          <td style={{ color: remaining < 0 ? "red" : "inherit" }}>
                                            {toDisplayValue(remaining)}{unitLabel}
                                          </td>
                                        );
                                      }

                                      case "hours": {

                                        const spent = subtask.totalSpent ?? 0;
                                        const max = subtask.totalMax ?? 0;
                                        const remaining = max - spent;

                                        const hoursPercent =
                                          max > 0
                                            ? Math.min(100, Math.round((spent / max) * 100))
                                            : 0;

                                        const isMatch = hoursPercent === subtask.percentCompleted;

                                        return (
                                          <td key={col.key} style={{ minWidth: "120px" }}>
                                            <Tooltip
                                              title={
                                                <>
                                                  <div>Max: {toDisplayValue(max)}{unitLabel}</div>
                                                  <div>Spent: {toDisplayValue(spent)}{unitLabel}</div>
                                                  <div>Rem: {toDisplayValue(remaining)}{unitLabel}</div>
                                                </>
                                              }
                                              arrow
                                            >
                                              <div className="d-flex flex-column gap-1">
                                                <Progress
                                                  value={hoursPercent}
                                                  max={100}
                                                  color={isMatch ? "primary" : "danger"}
                                                  style={{ cursor: "pointer" }}
                                                >
                                                  <strong>{hoursPercent}%</strong>
                                                </Progress>
                                              </div>
                                            </Tooltip>
                                          </td>
                                        );
                                      }

                                      case "assigned":
                                        return (
                                          <td key={col.key} data-label="assigned">
                                            {teamMembers.find(m => m.userID === subtask.assignedToId)?.name || '—'}
                                          </td>
                                        );
                                      case "assignedby":
                                        return (
                                          <td key={col.key}>
                                            {task.createdByName}
                                          </td>
                                        );
                                      case "status":
                                        return (
                                          <td key={col.key}>
                                            <Badge className={statusColorMap[subtask.status] || 'status-pending'}>
                                              {formatStatusLabel(subtask.status)}
                                            </Badge>
                                          </td>
                                        );
                                      case "priority":
                                        return (
                                          <td key={col.key}>
                                            <Badge className={priorityColorMap[subtask.priority] || 'priority-medium'}>
                                              {subtask.priority}
                                            </Badge>
                                          </td>
                                        );
                                      case "dueDate":
                                        return <td key={col.key}>{formatDate(subtask.dueDate)}

                                          <span
                                            className="text-black ms-1"
                                            title={subtask.isDueDateLocked ? "Due date is locked by admin" : ""}
                                          >
                                            {subtask.isDueDateLocked && <HttpsIcon fontSize="small" />}
                                          </span>
                                        </td>;
                                      case "completion": {
                                        if (!subtask.dueDate) return <td>—</td>;

                                        const due = new Date(subtask.dueDate);
                                        const today = new Date();
                                        const completed = subtask.completionDate
                                          ? new Date(subtask.completionDate)
                                          : null;

                                        const status = subtask.status?.toLowerCase();

                                        // normalize time
                                        due.setHours(0, 0, 0, 0);
                                        today.setHours(0, 0, 0, 0);
                                        completed?.setHours(0, 0, 0, 0);

                                        const isCompleted =
                                          status === "released" && completed !== null;

                                        const calculateDiff = (start: Date, end: Date) =>
                                          useWeekdays
                                            ? countWeekdaysSigned(start, end)
                                            : Math.round(
                                              (end.getTime() - start.getTime()) /
                                              (1000 * 60 * 60 * 24)
                                            );

                                        let diff = 0;
                                        let tooltipText = "";
                                        let displayText = "";

                                        // ✅ COMPLETED SUBTASK
                                        if (isCompleted) {
                                          diff = calculateDiff(due, completed!);

                                          if (diff === 0) {
                                            tooltipText = "Completed On Time";
                                          } else if (diff < 0) {
                                            tooltipText = `Completed Early by ${Math.abs(diff)} days`;
                                          } else {
                                            tooltipText = `Completed Late by ${diff} days`;
                                          }

                                          displayText = "Completed";
                                        }
                                        // ✅ ACTIVE SUBTASK
                                        else {
                                          // ✅ IMPORTANT: today first, due second
                                          diff = calculateDiff(today, due);

                                          if (diff === 0) {
                                            displayText = "0 day";
                                            tooltipText = "Due today";
                                          } else if (diff < 0) {
                                            displayText = `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
                                            tooltipText = `Overdue by ${Math.abs(diff)} days`;
                                          } else {
                                            displayText = `${diff} day${diff === 1 ? "" : "s"}`;
                                            tooltipText = `${diff} days remaining`;
                                          }
                                        }

                                        const color = getDueColor(diff, isCompleted);

                                        return (
                                          <td title={tooltipText} style={{ color, fontWeight: 600 }}>
                                            {displayText}
                                          </td>
                                        );
                                      }
                                      case "percent": {

                                        return (
                                          <td key={col.key} data-label="Percent" style={{ minWidth: "100px" }}>
                                            <Tooltip
                                              title={
                                                <>
                                                  <div>{subtask.percentCompleted}% completed</div>
                                                </>
                                              }
                                              arrow
                                            >
                                              <div className="d-flex flex-column gap-1">
                                                <Progress
                                                  value={subtask.percentCompleted ?? 0}
                                                  max={100}
                                                >
                                                </Progress>
                                              </div>
                                            </Tooltip>
                                          </td>
                                        );
                                      }


                                      case "comment": {
                                        const total = totalCommentCount[`subtask-${subtask.id}`] ?? 0;
                                        const unread = unreadCounts[`subtask-${subtask.id}`] ?? 0;

                                        return (
                                          <td data-label="Comment">
                                            <Button
                                              color="link"
                                              size="sm"
                                              className="position-relative"
                                              onClick={() => {
                                                openComments("subtask", subtask.id, subtask.description);

                                                setUnreadCounts(prev => {
                                                  const updated = { ...prev, [`subtask-${subtask.id}`]: 0 };
                                                  localStorage.setItem("unreadCounts", JSON.stringify(updated));
                                                  return updated;
                                                });
                                              }}

                                            >

                                              {/* 🟦 NO COMMENTS */}
                                              {total === 0 && (
                                                <ChatBubbleOutline fontSize="small" style={{ color: "blue", opacity: 0.6 }} />
                                              )}

                                              {/* 🟢 ALL READ */}
                                              {total > 0 && unread === 0 && (
                                                <Chat fontSize="small" style={{ color: "#0084ffff" }} />
                                              )}

                                              {/* 🔴 UNREAD COMMENTS */}
                                              {unread > 0 && (
                                                <>
                                                  <MarkChatUnread fontSize="small" style={{ color: "blue" }} />

                                                  <Badge
                                                    color="danger"
                                                    className="position-absolute top-0 start-100 translate-middle badge-pulse"
                                                    style={{
                                                      background: "#d32f2f",
                                                      color: "white",
                                                      padding: "3px 6px",
                                                      fontSize: "11px",
                                                      borderRadius: "16px",
                                                      border: "1.5px solid white",
                                                      fontWeight: 600,
                                                      boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                                                    }}
                                                  >
                                                    {unread}
                                                  </Badge>
                                                </>
                                              )}
                                            </Button>
                                            {/* <HelpOutline
                                                                                        style={{
                                                                                          cursor: "pointer"
                                                                                        }}
                                                                                        className="ms-1"
                                                                                        onClick={() => {
                                                                                          setSelectedEntity({ type: "subtask", id: subtask.id });
                                                                                          setInputModalOpen(true);
                                                                                        }}
                                                                                      >
                                                                                        <title>
                                                                                          {subtask.isClientInputEnabled
                                                                                            ? "Client input disabled"
                                                                                            : "Ask / View client input"}
                                                                                        </title>
                                      
                                                                                      </HelpOutline> */}

                                          </td>
                                        );
                                      }

                                      case "log":
                                        return (
                                          <td data-label="Log">
                                            <Button
                                              color="link"
                                              size="sm"
                                              className="p-0"  // 🔹 Removes extra padding, looks like a plain link
                                              onClick={() => {
                                                setSelectedLogEntity({ type: "subtask", id: subtask.id, title: subtask.description });
                                                loadOverallLogs("subtask", subtask.id);
                                              }}
                                            >
                                              {formatDate(subtask.completionDate)}
                                            </Button>
                                          </td>
                                        );

                                      default:
                                        return null;
                                    }
                                  })}
                                </tr>
                                {expandedSubTasks[subtask.id] && (
                                  <>
                                    {/* Loading row */}
                                    {loadingChildTasks.includes(subtask.id) && (
                                      <tr>
                                        <td colSpan={visibleOrderedColumns.length} className="text-center">
                                          <Spinner size="sm" /> Loading...
                                        </td>
                                      </tr>
                                    )}

                                    {/* Child subtasks */}
                                    {(childTasks[subtask.id] || []).map((ct) => (
                                      <tr key={ct.id} style={{ backgroundColor: "#f9fafb" }}>
                                        {visibleOrderedColumns.map((col) => {
                                          if (!visibleColumns.includes(col.key)) return null;

                                          switch (col.key) {
                                            case "number":
                                              return <td key={col.key}></td>;

                                            case "description":
                                              return (
                                                <td key={col.key} className="ps-5">
                                                  ↳ {ct.description}
                                                </td>
                                              );

                                            case "spentHours": {
                                              const spent = ct.spentHours ?? 0;

                                              return (
                                                <td>
                                                  <Button
                                                    color="link"
                                                    className="p-0 "
                                                    onClick={() =>
                                                      loadSpentHoursLogs(
                                                        "childSubTask",
                                                        ct.id,
                                                        ct.description
                                                      )
                                                    }
                                                  >
                                                    {toDisplayValue(spent)}{unitLabel}
                                                  </Button>
                                                </td>
                                              );
                                            }

                                            case "maxHours":
                                              return (
                                                <td>
                                                  {toDisplayValue(ct.maxHours ?? 0)}{unitLabel}
                                                </td>
                                              );

                                            case "Remaining": {
                                              const remaining =
                                                (ct.maxHours ?? 0) - (ct.spentHours ?? 0);
                                              return (
                                                <td style={{ color: remaining < 0 ? "red" : "inherit" }}>
                                                  {toDisplayValue(remaining)}{unitLabel}
                                                </td>
                                              );
                                            }

                                            case "hours": {
                                              const spent = ct.spentHours ?? 0;
                                              const max = ct.maxHours ?? 0;
                                              const remaining = max - spent;

                                              const percent = calculatePercent(spent, max);

                                              return (
                                                <td style={{ minWidth: "100px" }}>
                                                  <Tooltip
                                                    arrow
                                                    title={
                                                      <>
                                                        <div>Max: {toDisplayValue(max)}{unitLabel}</div>
                                                        <div>Spent: {toDisplayValue(spent)}{unitLabel}</div>
                                                        <div>Rem: {toDisplayValue(remaining)}{unitLabel}</div>
                                                      </>
                                                    }
                                                  >
                                                    <div>
                                                      <Progress
                                                        value={percent}
                                                        style={{ cursor: "pointer" }}
                                                      >
                                                        <strong>{percent}%</strong>
                                                      </Progress>
                                                    </div>
                                                  </Tooltip>
                                                </td>
                                              );
                                            }

                                            case "percent": {

                                              return (
                                                <td key={col.key} data-label="Percent" style={{ minWidth: "100px" }}>
                                                  <Tooltip
                                                    title={
                                                      <>
                                                        <div>{ct.percentCompleted}% completed</div>
                                                      </>
                                                    }
                                                    arrow
                                                  >
                                                    <div className="d-flex flex-column gap-1">
                                                      <Progress
                                                        value={ct.percentCompleted ?? 0}
                                                        max={100}
                                                      >
                                                      </Progress>
                                                    </div>
                                                  </Tooltip>
                                                </td>
                                              );
                                            }

                                            case "assigned":
                                              return (
                                                <td key={col.key}>
                                                  {ct.teamMemberNames && ct.teamMemberNames.length > 0 ? (
                                                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                                                      {ct.teamMemberNames.map((name: string, idx: number) => (
                                                        <UserAvatar key={idx} name={name} />
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <span className="text-muted">—</span>
                                                  )}
                                                </td>

                                              );

                                            // case "assignedby":
                                            //   return (
                                            //     <td key={col.key} data-label="Assigned By">
                                            //       {teamMembers.find(m => m.userID === ct.createdById)?.name
                                            //         || ct.createdAt
                                            //         || '—'}
                                            //     </td>
                                            //   );

                                            case "status":
                                              return (
                                                <td key={col.key}>
                                                  <Badge className={statusColorMap[ct.status] || "secondary"}>
                                                    {childformatStatusLabel(ct.status)}
                                                  </Badge>
                                                </td>
                                              );

                                            case "priority":
                                              return (
                                                <td key={col.key}>
                                                  <Badge className={priorityColorMap[ct.priority] || "secondary"}>
                                                    {ct.priority}
                                                  </Badge>
                                                </td>
                                              );

                                            case "dueDate":
                                              return <td key={col.key}>{formatDate(ct.dueDate)}</td>;

                                            case "completion": {
                                              if (!ct.dueDate) return <td>—</td>;

                                              const due = new Date(ct.dueDate);
                                              const today = new Date();
                                              const completed = ct.completionDate
                                                ? new Date(ct.completionDate)
                                                : null;

                                              const status = ct.status?.toLowerCase();

                                              // normalize time
                                              due.setHours(0, 0, 0, 0);
                                              today.setHours(0, 0, 0, 0);
                                              completed?.setHours(0, 0, 0, 0);

                                              const isCompleted =
                                                status === "released" && completed !== null;

                                              const calculateDiff = (start: Date, end: Date) =>
                                                useWeekdays
                                                  ? countWeekdaysSigned(start, end)
                                                  : Math.round(
                                                    (end.getTime() - start.getTime()) /
                                                    (1000 * 60 * 60 * 24)
                                                  );

                                              let diff = 0;
                                              let tooltipText = "";
                                              let displayText = "";

                                              // ✅ COMPLETED CHILD TASK
                                              if (isCompleted) {
                                                diff = calculateDiff(due, completed!);

                                                if (diff === 0) {
                                                  tooltipText = "Completed On Time";
                                                } else if (diff < 0) {
                                                  tooltipText = `Completed Early by ${Math.abs(diff)} days`;
                                                } else {
                                                  tooltipText = `Completed Late by ${diff} days`;
                                                }

                                                displayText = "Completed";
                                              }
                                              // ✅ ACTIVE CHILD TASK
                                              else {
                                                // ✅ IMPORTANT: today first, due second
                                                diff = calculateDiff(today, due);

                                                if (diff === 0) {
                                                  displayText = "0 day";
                                                  tooltipText = "Due today";
                                                } else if (diff < 0) {
                                                  displayText = `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
                                                  tooltipText = `Overdue by ${Math.abs(diff)} days`;
                                                } else {
                                                  displayText = `${diff} day${diff === 1 ? "" : "s"}`;
                                                  tooltipText = `${diff} days remaining`;
                                                }
                                              }

                                              const color = getDueColor(diff, isCompleted);

                                              return (
                                                <td title={tooltipText} style={{ color, fontWeight: 600 }}>
                                                  {displayText}
                                                </td>
                                              );
                                            }

                                            case "log":
                                              return (
                                                <td key={col.key}>
                                                  <Button
                                                    color="link"
                                                    size="sm"
                                                    className="p-0"
                                                    onClick={() => {
                                                      setSelectedLogEntity({
                                                        type: "child-subtasks",
                                                        id: ct.id,
                                                        title: ct.description
                                                      });
                                                      loadOverallLogs("childSubTask", ct.id);
                                                    }}
                                                  >
                                                    {formatDate(ct.completionDate)}
                                                  </Button>
                                                </td>
                                              );

                                            default:
                                              return <td key={col.key}></td>;
                                          }
                                        })}
                                      </tr>
                                    ))}
                                    <tr>
                                      <td
                                        colSpan={visibleOrderedColumns.length}
                                        style={{
                                          background: "#f5f7fb",
                                          padding: "0px",
                                          borderTop: "1px solid #d6dbe3"
                                        }}
                                      >
                                        <div
                                          style={{
                                            height: "470px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            padding: "15px",
                                            background: "#ffffff",
                                            borderRadius: "6px",
                                          }}
                                        >
                                          <Comments
                                            isOpen={false}
                                            onClose={() => { }}
                                            entityType="subtask"
                                            entityId={subtask.id}
                                            title={subtask.description}
                                            inlineMode={true}
                                          />
                                        </div>
                                      </td>
                                    </tr>

                                  </>
                                )}


                              </React.Fragment>
                            ))}

                      </React.Fragment>
                    ))}

                </tbody>
              </Table>
            </div>
          )}

          <Modal isOpen={remarksModalOpen} toggle={() => setRemarksModalOpen(false)} centered>
            <ModalBody>
              <textarea
                className="form-control border rounded bg-light text-dark"
                readOnly
                style={{ whiteSpace: 'pre-wrap', height: '300px', resize: 'vertical' }}
                value={selectedRemarks || ""}
              />
            </ModalBody>
          </Modal>

          {commentPopup?.open && (
            <Suspense fallback={null}>
              <Comments
                isOpen={commentPopup.open}
                onClose={() => {
                  // Refresh unread count only for task/subtask
                  if (commentPopup.type === "task" || commentPopup.type === "subtask") {
                    loadUnreadCountFor(commentPopup.type, commentPopup.id);
                  }
                  setCommentPopup(null);
                }}

                entityType={commentPopup.type}
                entityId={commentPopup.id}
                title={commentPopup.title}
              />
            </Suspense>

          )}

          <Modal
            isOpen={logModalOpen}
            toggle={() => setLogModalOpen(false)}
            centered
            size="xl"
          >
            <ModalHeader
              toggle={() => setLogModalOpen(false)}
              style={{
                background: "#343a40",
                color: "white",
                fontWeight: 600,
                fontSize: "18px",
                padding: "15px 20px",
              }}
            >
              Overall Log – {selectedLogEntity?.title || `${selectedLogEntity?.type} #${selectedLogEntity?.id}`}
            </ModalHeader>

            <ModalBody
              style={{
                background: "#f3f4f6",
                padding: "20px",
                borderRadius: "0 0 10px 10px",
              }}
            >
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "15px",
                  boxShadow: "0px 3px 10px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <h6
                  style={{
                    marginBottom: "15px",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#333",
                  }}
                >
                  Change History
                </h6>

                <Table
                  hover
                  responsive
                  bordered
                  style={{ marginBottom: 0, borderRadius: "10px" }}
                >
                  <thead
                    style={{
                      background: "#f0f2f5",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    <tr>
                      <th style={{ width: "15%" }}>Field</th>
                      <th style={{ width: "20%" }}>Old</th>
                      <th style={{ width: "20%" }}>New</th>
                      <th style={{ width: "20%" }}>User</th>
                      <th style={{ width: "20%" }}>Date</th>
                    </tr>
                  </thead>

                  <tbody style={{ fontSize: "14px" }}>
                    {overallLogs.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center"
                          style={{ padding: "25px", color: "#777" }}
                        >
                          No logs found.
                        </td>
                      </tr>
                    ) : (
                      overallLogs.map((log, index) => (
                        <tr
                          key={log.id ?? index}
                          style={{
                            background: index % 2 === 0 ? "#ffffff" : "#fafafa",
                          }}
                        >
                          {/* FIELD */}
                          <td style={{ fontWeight: 600, color: "#444" }}>
                            {log.fieldName}
                          </td>

                          {/* OLD VALUE */}
                          <td>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                              }}
                            >
                              {(!log.oldValue || log.oldValue === "00000000-0000-0000-0000-000000000000")
                                ? "-"
                                : log.oldValue}
                            </span>
                          </td>


                          {/* NEW VALUE */}
                          <td>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                              }}
                            >
                              {log.newValue ?? "-"}
                            </span>
                          </td>

                          {/* USER */}
                          <td style={{ fontWeight: 500 }}>{log.userName}</td>

                          {/* DATE */}
                          <td style={{ color: "#555", fontSize: "12px" }}>
                            {new Date(log.logTime).toLocaleDateString("en-GB")}{" "}
                            {new Date(log.logTime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </ModalBody>
          </Modal>




          <Modal isOpen={settingsOpen} toggle={() => setSettingsOpen(false)} centered>
            <ModalHeader toggle={() => setSettingsOpen(false)}>Settings</ModalHeader>
            <ModalBody>

              {/* Toggle Percent Column */}
              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={visibleColumns.includes("percent")}
                    onChange={() => {
                      setVisibleColumns(prev => {
                        const updated = prev.includes("percent")
                          ? prev.filter(c => c !== "percent")
                          : [...prev, "percent"];

                        localStorage.setItem("visibleColumns", JSON.stringify(updated)); // 🔥 Save permanently
                        return updated;
                      });
                    }}
                  />
                  <span className="ms-2">Show Progress Column</span>
                </Label>
              </div>
              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={visibleColumns.includes("hours")}
                    onChange={() => {
                      setVisibleColumns(prev => {
                        const updated = prev.includes("hours")
                          ? prev.filter(c => c !== "hours")
                          : [...prev, "hours"];

                        localStorage.setItem("visibleColumns", JSON.stringify(updated)); // 🔥 Save permanently
                        return updated;
                      });
                    }}
                  />
                  <span className="ms-2">Show Hours Column</span>
                </Label>
              </div>
              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={
                      visibleColumns.includes("spentHours") &&
                      visibleColumns.includes("maxHours") &&
                      visibleColumns.includes("Remaining")
                    }
                    onChange={() => {
                      setVisibleColumns(prev => {
                        const hasAll =
                          prev.includes("spentHours") &&
                          prev.includes("maxHours") &&
                          prev.includes("Remaining");

                        const updated = hasAll
                          ? prev.filter(
                            c => !["spentHours", "maxHours", "Remaining"].includes(c)
                          )
                          : [
                            ...prev,
                            ...["spentHours", "maxHours", "Remaining"].filter(
                              c => !prev.includes(c)
                            ),
                          ];

                        localStorage.setItem("visibleColumns", JSON.stringify(updated));
                        return updated;
                      });
                    }}
                  />
                  <span className="ms-2">Show Spent / Max </span>
                </Label>
              </div>

              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={showCompletedReleased}
                    onChange={() => {
                      setShowCompletedReleased(prev => {
                        const newValue = !prev;
                        localStorage.setItem("showCompletedReleased", String(newValue)); // 🔥 Save permanently
                        return newValue;
                      });
                    }}
                  />
                  <span className="ms-2">Show Released</span>
                </Label>
              </div>
              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={showNotInScope}
                    onChange={() => {
                      setshowNotInScope(prev => {
                        const newValue = !prev;
                        localStorage.setItem("showNotInScope", String(newValue)); // 🔥 Save permanently
                        return newValue;
                      });
                    }}
                  />
                  <span className="ms-2">Show Not In Scope</span>
                </Label>
              </div>
              <FormGroup check className="d-flex mb-2 align-items-center">
                <Label>
                  <Input
                    type="checkbox"
                    checked={showOnDays}
                    onChange={() => {
                      setShowOnDays(prev => {
                        const v = !prev;
                        localStorage.setItem("showOnDays", String(v));
                        return v;
                      });
                    }}
                  />
                  <span className="ms-2">Show in Hours</span>
                </Label>
              </FormGroup>

              <hr />
              <h6>Due Color Settings</h6>

              {Object.keys(dueColorSettings).map(key => (<>
                <div key={key} className="d-flex align-items-center mb-2">
                  <Label style={{ width: "160px" }}>{key}</Label>
                  <Input
                    type="color"
                    value={dueColorSettings[key]}
                    onChange={(e) => {
                      const updated = {
                        ...dueColorSettings,
                        [key]: e.target.value,
                      };
                      setDueColorSettings(updated);
                      localStorage.setItem("dueColorSettings", JSON.stringify(updated));
                    }}
                    style={{ width: "60px", height: "35px", padding: 2 }}
                  />


                </div>
              </>
              ))}

              <div className="mt-3">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setDueColorSettings(DEFAULT_DUE_COLORS);
                    localStorage.setItem(
                      "dueColorSettings",
                      JSON.stringify(DEFAULT_DUE_COLORS)
                    );
                  }}
                >
                  Reset to Default
                </Button>
              </div>




            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => {
                // Save to localStorage permanently
                localStorage.setItem("visibleColumns", JSON.stringify(visibleColumns));
                localStorage.setItem("showCompletedReleased", String(showCompletedReleased));
                localStorage.setItem("showNotInScope", String(showNotInScope));
                localStorage.setItem("useWeekdays", String(useWeekdays));

                setSettingsOpen(false); // close modal
              }}>
                Apply
              </Button>
            </ModalFooter>

          </Modal>

          <Modal
            isOpen={spentModalOpen}
            toggle={() => setSpentModalOpen(false)}
            centered
            size="lg"
          >
            <ModalHeader toggle={() => setSpentModalOpen(false)}>
              🕒 Spent Hours – {spentEntityTitle}
            </ModalHeader>

            <ModalBody>
              {spentLogs.length === 0 ? (
                <Alert color="info">No spent hour records found</Alert>
              ) : (
                <>
                  <Table bordered hover responsive>
                    <thead className="table-light">
                      <tr>
                        <th>User</th>
                        <th>Worked Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(userHourMap).map(
                        ([user, hours]) => (
                          <tr key={user}>
                            <td>{user}</td>
                            <td className="fw-bold">
                              {hours} H
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th className="text-success">
                          {totalWorked} H
                        </th>
                      </tr>
                    </tfoot>
                  </Table>

                  <hr />

                  {/* <h6 className="fw-bold">🧾 Detailed Log</h6>

        <Table size="sm" bordered>
          <thead>
            <tr>
              <th>User</th>
              <th>Old</th>
              <th>New</th>
              <th>Worked</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {spentLogs.map((log, i) => {
              const oldV = Number(log.oldValue ?? 0);
              const newV = Number(log.newValue ?? 0);

              return (
                <tr key={i}>
                  <td>{log.userName}</td>
                  <td>{oldV}</td>
                  <td>{newV}</td>
                  <td className="fw-semibold">
                    {newV - oldV} H
                  </td>
                  <td>
                    {new Date(log.logTime).toLocaleDateString("en-GB")}{" "}
                    {new Date(log.logTime).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table> */}
                </>
              )}
            </ModalBody>
          </Modal>



        </CardBody>
      </Card>

      {inputModalOpen && entity && userID && (
        <ClientInputModal
          isOpen={inputModalOpen}
          toggle={async () => {
            setInputModalOpen(false);
            setSelectedEntity(null);

            // 🔥 Ensure DB state is reflected after close
            if (selectedProjectId) {
              await fetchProjectTasks(selectedProjectId);
            }
          }}

          entityType={entity.type}
          entityId={entity.id}

          isClientInputEnabled={
            entity.type === "task"
              ? projects
                .find(p => p.id === selectedProjectId)
                ?.tasks?.find(t => t.id === entity.id)
                ?.isClientInputEnabled ?? false
              : projects
                .find(p => p.id === selectedProjectId)
                ?.tasks
                ?.flatMap(t => t.subTasks || [])
                .find(st => st.id === entity.id)
                ?.isClientInputEnabled ?? false
          }

          /** 🔥 THIS IS THE FIX THAT PREVENTS UNCHECK ON REOPEN */
          onToggle={(enabled: boolean) => {
            setProjects(prev =>
              prev.map(p =>
                p.id !== selectedProjectId
                  ? p
                  : {
                    ...p,
                    tasks: p.tasks?.map(t => {
                      // TASK
                      if (entity.type === "task" && t.id === entity.id) {
                        return { ...t, isClientInputEnabled: enabled };
                      }

                      // SUBTASK
                      if (entity.type === "subtask") {
                        return {
                          ...t,
                          subTasks: t.subTasks.map(st =>
                            st.id === entity.id
                              ? { ...st, isClientInputEnabled: enabled }
                              : st
                          )
                        };
                      }

                      return t;
                    })
                  }
              )
            );
          }}

          canCreate={true}
        />
      )}

    </Container>
  );
};

export default TeamLeadProjectsWithTasks;
